<template>
  <div id="app">
    <div :class="{'open': menuOpen, 'sidebar': true, 'about': showAbout }">
      <div class="toggle">
        <span class="navicon" @click="hidemenu"><i class="fas fa-times"></i></span>
      </div>
      <nav>
        <h3>Visualizations</h3>
        <ul>
          <li v-for="(item, index) in dataSets" v-bind:key="item.name" @click="changeDatasets(index)">{{ item.name }}</li>
        </ul>
        <p><a href="#about" @click.stop.prevent="showAbout = true">About</a></p>
      </nav>
      <div class="aboutWrapper">
        <p>“If you don’t find it, build it.”</p>
        <p>When it comes to data visualization, I’ve always been a bit curious why there wasn’t a nested bar chart interface where one could explore the figures/information that make up a specific piece of data. And how can it also be done in an engaging and interactive way?</p>
        <p>Voila! This is my go at it.</p>
        <p>I used 2010 census data about New York City’s population by borough and neighborhood, available on <a href="https://data.cityofnewyork.us/City-Government/New-York-City-Population-By-Neighborhood-Tabulatio/swpk-hqdp" target="_blank">NYC Open Data</a>. With this visualization, you can visually see the population of each neighborhood and how many people and all the neighborhoods that make up the millions of people who live in the five boroughs of New York City. I’m a little biased...NYC, specifically Brooklyn, has been my home since 2002!</p>
        <p>Taking a different data set, I gathered data about all <a href="https://www.best-selling-cars.com/" target="_blank">cars sold in the US in 2018</a> and visualized it as well. I personally like German cars and I had no idea that they had such a small market share in the US!</p>
        <p>This visualization is also a continuation of another design investigation I made a few years prior: <a href="https://www.cloudred.com/labprojects/nyctrees/" target="_blank">NYC Trees</a>. It’s safe to say that I like bar charts. I love its simplicity and the exploration of possibilities when it comes to this well-known method of graphing. </p>
        <p><a href="https://cloudred.com/contact">Get in touch with us</a> for feedback or suggestions! I’d love to hear from you.</p>
        <p><strong>Credits</strong><br>
        Concept &amp; Interaction design: Allen Yee of Cloudred / Programming: Cristian Zapata<br>
        Made with D3.js and Vue.js</p>
      </div>
    </div>
    <div class="overlay" v-if="menuOpen" @click="hidemenu"></div>
    <header>
      <div class="header">
        <div>
          <span class="nav" @click="menuOpen = true"><i class="fas fa-bars"></i></span>
        </div>
        <div>
          <h1>Nested Bar Charts</h1>
          <div class="info">
            <dl>
              <dt>On display:</dt><dd>{{ chartDescription }}</dd>
              <dt>Source:</dt><dd><a :href="chartSourceLink">{{ chartSource }}</a></dd>
            </dl>
          </div>
        </div>
      </div>
      <div class="social">
        <h3>Follow us:</h3>
        <a href="https://twitter.com/cloudred" target="_blank"><i class="fab fa-twitter-square"></i></a>
        <a href="https://www.instagram.com/cloudredxr/" target="_blank"><i class="fab fa-instagram"></i></a>
      </div>
        
    </header>
    <div class="chart">
      <svg id="bars">
      </svg>
    </div>
    <footer>
      <p>&copy; Copyright 2019. Cloudred Multimedia LLC. All Rights Reserved.</p>
      <div class="brand" style="visibility: inherit; opacity: 1;">
        <a href="https://www.cloudred.com"><span class="sr-only">Cloudred home</span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.47 49.65"><title>cloudred-logo</title><path class="letter letter--c" d="M41.15 42.23c-5.49 5.14-10.63 7.21-17.68 7.21S10.63 47 6.42 42.3A26.35 26.35 0 0 1-.01 24.83C-.01 10.35 9.85 0 23.61 0c7.38 0 12.38 2.07 18 7.56L35.26 14c-3.85-3.49-7.13-4.92-11.13-4.92-8.34-.02-14.14 6.4-14.14 15.53s6.13 15.77 14.69 15.77a13.15 13.15 0 0 0 10.63-4.85z" fill="#ffffff"></path><path class="letter letter--l" d="M47.14 48.51V.93h9.06v47.58z" fill="#fff"></path><path class="letter letter--o letter--animated" d="M98.99 18.91c0 9.7-7.92 17.33-18 17.33-10.41 0-17.9-7.35-17.9-17.62 0-10.49 7.35-17.69 18-17.69 10.38 0 17.9 7.56 17.9 17.98zm-26.82-.29c0 5.71 3.57 9.56 8.84 9.56s8.85-3.92 8.85-9.42c0-6-3.35-9.7-8.77-9.7s-8.91 3.78-8.91 9.56z" fill="#ffffff"></path><path class="letter letter--u letter--animated" d="M133.99 2.07V23c0 4.85-1 7.56-3.71 9.85a16.44 16.44 0 0 1-10.7 3.42c-4.56 0-8.49-1.28-11.05-3.64-2.36-2.14-3.5-5.28-3.5-9.63V2.07h9.06v20.37c0 3.71 2.21 6.21 5.49 6.21s5.41-2.14 5.41-5.65V2.11z" fill="#ffffff"></path><path class="letter letter--d" d="M167.69.93h9.06v47.58h-9.06v-4.35c-2.85 3.92-6 5.49-11.06 5.49-9.77 0-16.33-7-16.33-17.33 0-5.57 1.85-10.2 5.28-13.41a16.47 16.47 0 0 1 11.2-4.57 13.87 13.87 0 0 1 10.91 4.78zm-17.76 31.51c0 5.71 3.78 10 8.91 10s8.85-4.36 8.85-10.06c0-6-3.71-10.35-8.77-10.35s-8.99 4.29-8.99 10.41z" fill="#ffffff"></path><path class="letter letter--r letter--animated" d="M194.72 2.07v4.85c.93-2.42 1.57-3.42 2.93-4.42a9.41 9.41 0 0 1 5.34-1.57 7.59 7.59 0 0 1 1.07.07v9.27c-.72-.07-1.43-.14-1.93-.14a7.31 7.31 0 0 0-6.06 3c-1.07 1.5-1.36 2.92-1.36 6.06V35.1h-9.06v-33z" fill="#ffffff"></path><path class="letter letter--e letter--animated" d="M216.69 20.76a8.82 8.82 0 0 0 16.3 3.14l8.34 2.28a17.54 17.54 0 0 1-16.05 10.06c-10.49 0-18-7.42-18-17.69S214.46.93 224.66.93c10.56 0 17.91 7.42 17.91 18.05l-.08 1.78zm16.55-5.21a8 8 0 0 0-8.42-7c-4.35 0-7.27 2.5-8.13 7z" fill="#ffffff"></path><path class="letter letter--d" d="M274.4.93h9.07v47.58h-9.06v-4.35c-2.86 3.92-6 5.49-11.06 5.49-9.77 0-16.34-7-16.34-17.33 0-5.57 1.86-10.2 5.28-13.41a16.54 16.54 0 0 1 11.18-4.57 13.91 13.91 0 0 1 10.92 4.78zm-17.76 31.51c0 5.71 3.78 10 8.91 10s8.85-4.36 8.85-10.06c0-6-3.71-10.35-8.78-10.35s-8.98 4.29-8.98 10.41z" fill="#ffffff"></path></svg></a>
      </div>
    </footer>
  </div>
</template>

<script>
import * as d3 from 'd3'
import axios from 'axios'
import { setTimeout } from 'timers';
export default {
  name: 'app',
  data() {
    return {
      menuOpen: false,
      showAbout: false,
      nodeSelected: false,
      nodeSelectedLevel: null,
      nodeSelectedIndex: null,
      resizeTimer: null,
      processedData: null,
      chartName: '',
      chartDescription: '',
      chartSource: '',
      chartSourceLink: '',
      currentLevel: 1,
      colorSet: d3.scaleOrdinal(d3.schemeSet3),
      colors: [
        '#1a535c',
        '#4ecdc4',
        '#f7fff7',
        '#ff6b6b',
        '#ffe66d'
      ],
      viz: {
        width: null,
        height: null,
        margins: {
          vertical: 0,
          horizontal: 200
        }
      },
      bar: {
        height: 50,
        angle: 40,
        depth: 90
      },
      axisValues: [
        1000000,
        200000
      ],
      dataSets: [],
      selectedDataset: null
    }
  },
  mounted () {
    this.loadData()
    this.resizeChart()
  },
  created () {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    hidemenu () {
      this.menuOpen = false
      setTimeout(() => {
        this.showAbout = false
      }, 300)
    },
    loadData () {
      axios.all([
        axios.get('./data/population.json'),
        axios.get('./data/cars.json')
      ])
      .then(axios.spread((pop, cars) => {
        this.dataSets.push(pop.data)
        this.dataSets.push(cars.data)
        let dataIndex = 0
        if (this.$route.query.data) {
          dataIndex = this.$route.query.data
        }
        this.changeDatasets(dataIndex)
      }))
      .catch(() => {
        // console.error('Axios or processing response error...', errorOrCancel)
      })
    },
    changeDatasets (index) {
      this.$router.push({
          name: '',
          query: {
            data: index
          }
      })
      this.selectedDataset = this.dataSets[index]
      this.clearGraph()
      this.drawBars()
      this.menuOpen = false
    },
    clearGraph () {
      const svg = d3.select('#bars')
      this.nodeSelected = false
      this.nodeSelectedIndex = null
      this.nodeSelectedLevel = null
      svg.selectAll('*').remove()
    },
    resizeChart () {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(this.doTheResize, 250)
    },
    doTheResize () {
      this.clearGraph()
      const width = parseInt(d3.select('#app').style('width'))
      const screenHeight = parseInt(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeigh)
      var svg = d3.select('#bars')
      svg
        .attr('width', width)
        .attr('height', screenHeight)
      this.viz.center = {x: Math.round(parseInt(svg.attr('width')) / 2), y: Math.round(svg.attr('height')) / 2}
      this.viz.width = parseInt(svg.attr('width'))
      this.viz.height = parseInt(svg.attr('height'))
      this.drawBars()
    },
    getData () {
      const doc = this.selectedDataset
      this.chartName = doc.name
      this.chartDescription = doc.description
      this.chartSource = doc.sourceName
      this.chartSourceLink = doc.sourceLink
      return doc.data
    },
    colorForNode (label, index) {
      switch (label) {
        case 'Bronx':
          return [this.colors[0], d3.color(this.colors[0]).brighter(2), d3.color(this.colors[0]).darker(2)]
        case 'Brooklyn':
          return [this.colors[1], d3.color(this.colors[1]).brighter(3), d3.color(this.colors[1]).darker(1)]
        case 'Manhattan':
          return [this.colors[2], this.colors[2], d3.color(this.colors[2]).darker(4)]
        case 'Queens':
          return [this.colors[3], d3.color(this.colors[3]).brighter(3), d3.color(this.colors[3]).darker(2)]
        case 'Staten Island':
          return [this.colors[4], d3.color(this.colors[4]).brighter(3), d3.color(this.colors[4]).darker(2)]
      }
      const color = this.colorSet(index)
      return [color, d3.color(color).brighter(3), d3.color(color).darker(2)]
    },
    drawBars () {
      if (!this.selectedDataset) {
        return
      }
      const data = this.getData()
      this.drawAxis(data, 1)
      this.drawBar(data, 1)
    },
    drawAxis (data, level) {
      const total = data.reduce((sum, node) => sum + node.total, 0)
      const slicesize = level === 1 ? this.axisValues[0] : this.axisValues[1]
      const divisions = Math.ceil(total / slicesize)
      const svg = d3.select('#bars')
      const margin = this.viz.margins.horizontal
      const origin = {'x': margin + this.bar.angle, 'y': this.viz.height - this.viz.center.y - this.bar.angle }
      const maxWidth = this.viz.width - (margin * 2)
      const spacing = maxWidth / divisions
      const axisHeight = 200

      var lineGroup = d3.select('g.axisGroup')
      var lineGenerator = d3.line()
      if (lineGroup.empty()) {
        lineGroup = svg.insert('g').lower()
          .attr('width', this.viz.width)
          .attr('height', this.viz.height)
          .attr('x', 0)
          .attr('y', 0)
          .attr('class', 'axisGroup')
        lineGroup.append('path')
          .attr('d', lineGenerator([
              [origin.x, origin.y],
              [origin.x + maxWidth, origin.y]
            ]))
          .attr('class', 'axis')
      }
      const ticks = lineGroup.selectAll('.tick')
     
      if (!ticks.empty()) {
        if (ticks.size() > divisions) {
          const tokeep = ticks.filter(function (d, i) { return i <= divisions })
          const toremove = ticks.filter(function (d, i) { return i > divisions })
          toremove.transition('remove')
            .duration(450)
            .ease(d3.easeCubicInOut)
            .attr('transform', 'translate(600,0)')
            .attr('opacity', 0)
            .remove()
          tokeep.transition('keep')
            .duration(450)
            .ease(d3.easeCubicInOut)
            .attr('transform', (d, i, n) => {
              const Ox = origin.x + (spacing * i)
              const box = n[i].getBBox()
              return 'translate(' + (Ox - (box.x + box.width)) + ',0)'
            })
          tokeep.selectAll('text').text((d, e, x) => {
            const i = parseInt(d3.select(x[e]).attr('axisIndex'))
            return this.labelForAxis(i, slicesize, level)
            
          })
        } else {
          ticks.transition('keep')
            .duration(450)
            .ease(d3.easeCubicInOut)
            .attr('transform', (d, i, n) => {
              const Ox = origin.x + (spacing * i)
              const box = n[i].getBBox()
              return 'translate(' + (Ox - (box.x + box.width)) + ',0)'
            })
          ticks.selectAll('text').text((d, e, x) => {
            const i = parseInt(d3.select(x[e]).attr('axisIndex'))
            return this.labelForAxis(i, slicesize, level)
          })
          const lastIndex = ticks.size()
          for (let i = lastIndex; i <= divisions; i++) {
            this.drawSingleAxis(origin, spacing, i, axisHeight, slicesize, lineGroup, lineGenerator, level)
          }
        }
        return
      }
      for (let i = 0; i <= divisions; i++) {
        this.drawSingleAxis(origin, spacing, i, axisHeight, slicesize, lineGroup, lineGenerator, level)
      }
    },
    labelForAxis (index, slicesize, level) {
      if (level === 1) {
        return index + 'M'
      }
      if ((slicesize * index) > 999999) {
        return ((slicesize * index) / 1000000).toPrecision(2) + 'M'
      }
      if (index > 0) {
        return (index * 100) + 'K'
      }
      return 0
    },
    drawSingleAxis (origin, spacing, i, axisHeight, slicesize, lineGroup, lineGenerator, level) {
      const Ox = origin.x + (spacing * i)
      const tickContainer = lineGroup.append('g').attr('class', 'tick').attr('opacity', 1)
      tickContainer.append('path')
      .attr('d', lineGenerator([
          [Ox, origin.y],
          [Ox, origin.y - axisHeight]
        ]))
      .attr('class', 'axis')

      var angle = 135
      var x = Math.cos(angle * Math.PI / 180) * axisHeight + (Ox)
      var y = Math.sin(angle * Math.PI / 180) * axisHeight + (origin.y)
      tickContainer.append('path')
      .attr('d', lineGenerator([
          [Ox, origin.y],
          [x, y]
        ]))
      .attr('class', 'axis')
      tickContainer.append('text')
        .attr('class', 'axis label')
        .attr('pointer-events', 'none')
        .attr('width', 100)
        .attr('x', Ox - 10)
        .attr('y', origin.y - axisHeight + 10)
        .style('display','inline')
        .attr('text-anchor', 'end')
        .attr('axisIndex', i)
        .text(() => {
          return this.labelForAxis(i, slicesize, level)
        })
    },
    drawSingleBar (node, data, container, barIndex, width, origin, level, barColor) {
      const cap = this.getFacetFromOrigin(origin, width)
      const roof = this.getFacetRoofFromOrigin(origin, width)
      const front = this.getFacetFrontFromOrigin(origin, width)
      const hasNodesClass = node.nodes ? 'face' : ''
      container.selectAll('polygon')
         .data([roof, front, cap])
        .enter().append('polygon')
        .attr('class', function (d, i) {
          return hasNodesClass + ' bar level-' + level + '-' + barIndex + ' ' + 'side-' + i
        })
        .attr('points', function(poly) { 
          return poly.map(function(d) {
              return [d.x, d.y].join(',')
          }).join(' ')
        })
        .attr('fill', (_ , i) => {
          var color = this.colorForNode(node.label, barIndex)
          if (barColor) {
            color = barColor
            if (i !== 1) {
              return d3.color(color).darker()
            }
            return color
          }
          if (i !== 1) {
            return color[2]
          }
          return color[0]
        })
        .attr('stroke','hsla(0,0%,80%,1')
        .attr('stroke-width',1)
        .attr('stroke-linejoin','round')
        .attr('opacity', 1)
    },
    drawBar (data, level) {
      const svg = d3.select('#bars')
      const margin = this.viz.margins.horizontal
      const origin = {'x': margin, 'y': this.viz.height - this.viz.center.y }
      const total = data.reduce((sum, node) => sum + node.total, 0)
      const maxWidth = this.viz.width - (margin * 2)
      const slicesize = level === 1 ? this.axisValues[0] : this.axisValues[1]
      const divisions = Math.ceil(total / slicesize)
      const roundedMax = divisions * slicesize
      const mainBarContainer = svg.append('g')
        .attr('class', 'main-level-' + level)
        .attr('opacity', 1)
      data.sort((a, b) => {
          let comparison = 0
          if (a.label > b.label) {
            comparison = 1
          } else if (a.label < b.label) {
            comparison = -1
          }
          return comparison
        }).forEach((node, index) => {
        const width = Math.round(maxWidth * (node.total / roundedMax))
        const offsetTotals = data.slice(0, index).reduce((sum, current) => sum + current.total, 0)
        const offsetsSum = offsetTotals !== 0 ? Math.round(maxWidth * (offsetTotals / roundedMax)) : 0
        const offsetOrigin = {'x': origin.x + offsetsSum, 'y': origin.y}

        const baseContainer = mainBarContainer
          .append('g')
          .datum(node)
          .attr('class', 'bar-level-' + level)
          .attr('opacity', 1)
        this.drawSingleBar(node, data, baseContainer, index, width, offsetOrigin, level)
        baseContainer.selectAll('.bar')
          .on('mouseover', () => {
            this.updateText(node, level, index)
            if (node.nodes) {
              this.hilite('level-' + level + '-' + index)
            }
          })
          .on('mouseout', () => {
            this.resetText()
            if (node.nodes) {
              this.dehilite('level-' + level + '-' + index)
            }
          })
          .on('click', () => {
            if (node.nodes) {
              if (this.nodeSelected) {
                this.switchNodes(index, data)
              } else {
                this.selectNextLevel(index, level, data)
              }
            }
          })
        this.addText(origin, offsetOrigin, width, index, node, baseContainer, level)
      })
    },
    updateText (node, level, index) {
      if (this.nodeSelectedLevel && this.nodeSelectedLevel === level) {
        return
      }
      const svg = d3.select('#bars')
      const cloneContainer = svg.append('g')
        .attr('class', 'cloneTextContainer')
        .attr('opacity', 1)
      const up = index % 2 != 0
      const total = node.total.toLocaleString('en-US')
      var text = node.label
      if (up) {
        text = '——  ' + text + ' [' + total + ']'
      } else {
        text = '[' + total + '] ' + text + '  ——'
      }
  
      const textNode = d3.select('#label-' + level +'-'+ index).clone().remove()
      cloneContainer.append(function() {
        return textNode.node()
      })
      textNode.clone()
        .attr('id', null)
        .text(text)
        .attr('pointer-events', 'none')
      textNode.classed('cloneText', true)
        .attr('id', null)
        .attr('stroke', 'black')
        .attr('stroke-width', 9)
        .text(text)
    },
    resetText () {
      d3.selectAll('.cloneTextContainer').remove()
    },
    addText (origin, offsetOrigin, width, index, node, container, level) {
      var yPos = origin.y + 10
      var xPos = offsetOrigin.x + (width * 0.5)
      var rotate = 315
      var textAnchor = 'end'
      var text = node.label
      const up = index % 2 != 0
      if (up) {
        yPos = origin.y - 95
        xPos = offsetOrigin.x + (width * 0.5) + this.bar.angle
        rotate = '-45'
        textAnchor = 'start'
        text = '——  ' + text
      } else {
        text = text + '  ——'
      }
      container.append('text')
        .datum(node)
        .attr('class', 'label')
        .attr('id', 'label-' + level + '-' + index)
        .attr('width', 100)
        .attr('transform', 'translate('+ xPos +','+ yPos+'), rotate(' + rotate + ')')
        .style('display', () => { return 'inline' })
        .attr('text-anchor',  textAnchor)
        .text(text)
        .attr('opacity', (level === 2 ? 0 : 1))
    },
    selectNextLevel (index, level, data) {
      this.nodeSelected = true
      this.nodeSelectedLevel = level
      this.nodeSelectedIndex = index
      this.shrinkMain()
      this.cloneBar(index, level, data)
    },
    updateAxis (data, level) {
      this.drawAxis(data, level)
    },
    switchNodes (index, data) {
      if (index === this.nodeSelectedIndex) {
        this.reset(index, data)
        return
      }
      this.shrinkSelected(this.nodeSelectedIndex, data)
      this.cloneBar(index, this.nodeSelectedLevel, data, true)
      this.nodeSelectedIndex = index
    },
    reset (index, data) {
      this.shrinkSelected(this.nodeSelectedIndex, data)
      this.nodeSelected = false
      this.nodeSelectedIndex = null
      this.drawAxis(data, 1)
      const transition = d3.transition('select')
        .duration(450)
        .ease(d3.easeCubicInOut)
        
      transition.select('.main-level-1')
        .attr('transform', 'scale(1)translate(0 0)')

      transition.selectAll('.bar-level-1')
        .attr('opacity', 1)

    },
    shrinkSelected (index, data) {
      const scale = 0.7
      const offsetX = (this.viz.width) * 0.5
      const offsetY = ((this.viz.height) + 200) * 0.5

      const margin = 100
      const origin = {'x': margin, 'y': this.viz.height - this.viz.center.y }
      const maxWidth = this.viz.width - (margin * 2)
      const total = data.reduce((sum, node) => sum + node.total, 0)
      const node = data[index]
      const slicesize = this.axisValues[0] 
      const divisions = Math.ceil(total / slicesize)
      const roundedMax = divisions * slicesize
      const cloneWidth = Math.round(maxWidth * (node.total / roundedMax))

      const offsetTotals = data.slice(0, index).reduce((sum, current) => sum + current.total, 0)
      const offsetsSum = offsetTotals !== 0 ? Math.round(maxWidth * (offsetTotals / roundedMax)) : 0
      const cloneOffsetOrigin = {'x': origin.x + offsetsSum, 'y': origin.y}

      const mainBarContainer = d3.select('.clone-level-' + index).attr('opacity',1)
      const nData = node.nodes.filter(n => n.total > 0)
      mainBarContainer.selectAll('.cloneGroup')
        .each((n, i, x) => {
          const width = Math.round(cloneWidth * (n.total / node.total))          
          const offsetTotals = nData.slice(0, i).reduce((sum, current) => sum + current.total, 0)
          const offsetsSum = offsetTotals !== 0 ? Math.round(cloneWidth * (offsetTotals / node.total)) : 0
          const offsetOrigin = {'x': cloneOffsetOrigin.x + offsetsSum, 'y': cloneOffsetOrigin.y}
          d3.select(x[i]).selectAll('.bar').transition()
          .duration(450)
          .ease(d3.easeCubicInOut)
          .attr('stroke','hsla(0,0%,80%,0')
          .attr('points', (_, ix) => {
            var points = []
            switch (ix) {
              case 0: 
                points = this.getFacetRoofFromOrigin(offsetOrigin, width)
                break
              case 1: 
                points = this.getFacetFrontFromOrigin(offsetOrigin, width)
                break
              case 2: 
                points = this.getFacetFromOrigin(offsetOrigin, width)
                break
            }
            return points.map(function(d) {
                  return [d.x, d.y].join(',')
              }).join(' ')
          })
          d3.select(x[i]).selectAll('.bar').transition('xxx')
          .delay(150)
          .duration(300)
          .ease(d3.easeCubicInOut)
          .attr('opacity',0)
          .remove()

        })
      const transition = d3.transition('select')
        .duration(450)
        .ease(d3.easeCubicInOut)
      transition.select('.clone-level-' + index)
        .attr('transform', 'scale('+ scale +')translate(' + offsetX + ' ' + offsetY +')')
          .attr('opacity',0)
        .remove()

    },
    cloneBar (index, level, data, scaled) {
      const svg = d3.select('#bars')
      const margin = 200
      const origin = {'x': margin, 'y': this.viz.height - this.viz.center.y }
      const total = data.reduce((sum, node) => sum + node.total, 0)
      const node = data[index]
      const maxWidth = this.viz.width - (margin * 2)

      const slicesize = level === 1 ? this.axisValues[0] : this.axisValues[1]
      const divisions = Math.ceil(total / slicesize)
      const roundedMax = divisions * slicesize
      const cloneWidth = Math.round(maxWidth * (node.total / roundedMax))
      
      const offsetTotals = data.slice(0, index).reduce((sum, current) => sum + current.total, 0)
      const offsetsSum = offsetTotals !== 0 ? Math.round(maxWidth * (offsetTotals / roundedMax)) : 0
      const cloneOffsetOrigin = {'x': origin.x + offsetsSum, 'y': origin.y}

      const mainBarContainer = svg.append('g').attr('class', 'clone-level-' + index)
      const nData = node.nodes.filter(n => n.total > 0)
      const allTotals = nData.map(n => n.total)
      var minValue = Math.min.apply(null, allTotals)
      var maxValue = Math.max.apply(null, allTotals)
      var paletteScale = d3.scaleLinear()
        .domain([minValue, maxValue])
        .range([this.colorForNode(node.label, index)[0], this.colorForNode(node.label, index)[2]])

      nData.sort((a, b) => {
          let comparison = 0
          if (a.label > b.label) {
            comparison = 1
          } else if (a.label < b.label) {
            comparison = -1
          }
          return comparison
        }).forEach((n, index) => {
        const width = Math.round(cloneWidth * (n.total / node.total))
        const offsetTotals = nData.slice(0, index).reduce((sum, current) => sum + current.total, 0)
        const offsetsSum = offsetTotals !== 0 ? Math.round(cloneWidth * (offsetTotals / node.total)) : 0
        const offsetOrigin = {'x': cloneOffsetOrigin.x + offsetsSum, 'y': cloneOffsetOrigin.y}
        const baseContainer = mainBarContainer
          .append('g')
          .attr('class', 'cloneGroup bar-level-' + level)
          .attr('opacity', 1)
          .datum(n)
        const color = paletteScale(n.total)
        this.drawSingleBar(n, data, baseContainer, index, width, offsetOrigin, 2, color)
        baseContainer.selectAll('.bar')
          .on('click', () => {
            // if (node.nodes) {
            //   this.shrinkClone()
            // }
          })
          .on('mouseover', () => {
            this.updateText(n, 2, index)
          })
          .on('mouseout', () => {
            this.resetText()
          })
        this.addText(origin, offsetOrigin, width, index, n, baseContainer, 2)
      })

      const targetSliceSize = this.axisValues[1]
      const targetDivisions = Math.ceil(node.total / targetSliceSize)
      const targetRoundedMax = targetDivisions * targetSliceSize
      const targetWidth = Math.round(maxWidth * (node.total / targetRoundedMax))

      mainBarContainer.selectAll('.cloneGroup')
        .each((n, i, x) => {
          const width = Math.round(targetWidth * (n.total / node.total))          
          const offsetTotals = nData.slice(0, i).reduce((sum, current) => sum + current.total, 0)
          const offsetsSum = offsetTotals !== 0 ? Math.round(targetWidth * (offsetTotals / node.total)) : 0
          const offsetOrigin = {'x': origin.x + offsetsSum, 'y': origin.y}

          var yPos = origin.y + 10
          var xPos = offsetOrigin.x + (width * 0.5)
          var rotate = 315
          d3.select(x[i]).select('text').transition('p')
          .duration(650)
          .attr('transform', (a, b, c) => {
            const textAnchor = d3.select(c[b]).attr('text-anchor')
            if (textAnchor === 'start') {
              yPos = origin.y - 95
              xPos = offsetOrigin.x + (width * 0.5) + this.bar.angle
              rotate = '-45'
            }
            return 'translate('+ xPos +','+ yPos+'), rotate(' + rotate + ')'
          })
          
          d3.select(x[i]).select('text').transition('o')
          .delay(300)
          .duration(250)
          .attr('opacity', 1)

          d3.select(x[i]).selectAll('.bar').transition()
          .duration(650)
          .attr('points', (_, ix) => {
            var points = []
            switch (ix) {
              case 0: 
                points = this.getFacetRoofFromOrigin(offsetOrigin, width)
                break
              case 1: 
                points = this.getFacetFrontFromOrigin(offsetOrigin, width)
                break
              case 2: 
                points = this.getFacetFromOrigin(offsetOrigin, width)
                break
            }
            return points.map(function(d) {
                  return [d.x, d.y].join(',')
              }).join(' ')
          })
        })
      this.drawAxis(nData, 2)
      if (scaled) {
        const scale = 0.7
        const offsetX = (this.viz.width) * 0.5
        const offsetY = ((this.viz.height) + 200) * 0.5
        d3.select('.clone-level-' + index)
          .attr('transform', 'scale('+ scale +')translate(' + offsetX + ' ' + offsetY +')')
        const transition = d3.transition('unscale')
          .duration(650)
          .ease(d3.easeCubicInOut)
        transition.select('.clone-level-' + index)
          .attr('transform', 'scale(1)translate(0 0)')
      }
    },
    shrinkMain () {
      const scale = 0.7
      const offsetX = (this.viz.width) * 0.5
      const offsetY = ((this.viz.height) + 200) * 0.5
      const transition = d3.transition('select')
        .duration(450)
        .ease(d3.easeCubicInOut)
        
      transition.select('.main-level-1')
        .attr('transform', 'scale('+ scale +')translate(' + offsetX + ' ' + offsetY +')')

      transition.selectAll('.bar-level-1')
        .attr('opacity', 0.4)
    },
    shrinkClone () {
      this.hideMain()
      const scale = 0.7
      const offsetX = (this.viz.width) * 0.5
      const offsetY = ((this.viz.height) + 200) * 0.5
      const transition = d3.transition('select')
        .duration(450)
        .ease(d3.easeCubicInOut)
        
      transition.select('.clone-level-2')
        .attr('transform', 'scale('+ scale +')translate(' + offsetX + ' ' + offsetY +')')

      transition.selectAll('.clone-level-2 .bar-level-1')
        .attr('opacity', 0.4)
    },
    hideMain () {
      const scale = 0.5
      const offsetX = (this.viz.width)
      const offsetY = ((this.viz.height) + 200) * 1.2
      const transition = d3.transition('select')
        .duration(450)
        .ease(d3.easeCubicInOut)
        
      transition.select('.main-level-1')
        .attr('opacity', 0)
        .attr('transform', 'scale('+ scale +')translate(' + offsetX + ' ' + offsetY +')')
    },
    drawIndexed (index, data) {
      d3.selectAll('.main-level-2').remove()
      this.drawBar(data[index].nodes.filter(n => n.total > 0), 2, 'NTA Name')
      d3.selectAll('.axis').remove()
      this.drawAxis(data[index].nodes, 2)
    },
    hilite (className) {
      const transition = d3.transition('hilite')
        .duration(250)
      transition.selectAll('.' + className)
        .attr('opacity', () => { return 0.5 })
    },
    dehilite (className) {
      const transition = d3.transition('hilite')
        .duration(250)
      transition.selectAll('.' + className)
        .attr('opacity', () => { return 1 })
    },
    getFacetFromOrigin (origin, offset) {
      offset = offset || 0
      return [{'x':origin.x + offset, 'y': origin.y},
              {'x':origin.x + offset, 'y': origin.y - this.bar.height},
              {'x':origin.x + offset + this.bar.angle, 'y': origin.y - this.bar.depth},
              {'x':origin.x + offset + this.bar.angle, 'y': origin.y - this.bar.angle}]
    },
    getFacetRoofFromOrigin (origin, offset) {
      return [{'x':origin.x, 'y': origin.y - this.bar.height},
              {'x':origin.x + this.bar.angle, 'y': origin.y - this.bar.depth},
              {'x':origin.x + this.bar.angle + offset, 'y': origin.y - this.bar.depth},
              {'x':origin.x + offset, 'y': origin.y - this.bar.height}]
    },
    getFacetFrontFromOrigin (origin, offset) {
      return [origin,
              {'x':origin.x, 'y': origin.y - this.bar.height},
              {'x':origin.x + offset, 'y': origin.y - this.bar.height},
              {'x':origin.x + offset, 'y': origin.y}]
    },
    groupBy (array, prop) {
      return array.reduce(function (groups, item) {
        const val = item[prop]
        groups[val] = groups[val] || []
        groups[val].push(item)
        return groups
      }, {})
    }
  }
}
</script>

<style lang="scss">
  body, html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: black;
    color: white;
    margin: 0;
  }
  a {
    color: white;
  }
  #app {
    font-family: 'PT Sans Narrow', sans-serif;
    .chart {
      font-family: 'Avenir', Helvetica, Arial, sans-serif;
    }
  }
  .sidebar {
    background: #1b1a59;
    width:230px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: -230px;
    z-index: 20;
    transition: all 0.25s ease-in-out;
    &.open {
      left: 0;
    }
    &.open.about {
      left: 0;
    }
    .toggle {
      padding: 20px;
      .navicon {
        font-size: 1.5rem;
        line-height: 1;
        cursor: pointer;
        i {
          transition: all 0.2s ease-in-out;
        }
        
        &:hover i {
          transform: scale(1.2)
        }
      }
    }
    &.about {
      width:650px;
      left: -650px;
      nav {
        display: none;
      }
      .aboutWrapper {
        display: block;
      } 
    }
    .aboutWrapper {
      display: none;
      padding: 30px 40px 40px 20px;
    } 
    nav {
      font-size: 1rem;
      font-weight: normal;
      padding: 10px 20px;
      a {
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
      h3 {
        font-weight: normal;
        font-size: 1rem;
        line-height: 1;
        margin: 0 0 15px;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
        li {
          padding:  0 0 0 15px;
          margin: 0 0 10px;
          &:hover {
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }
    }
  }
  .overlay {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
  }
  footer {
    background: black;
    position: fixed;
    bottom: 20px; 
    left: 20px;
    right: 20px;
    z-index: 10;
    display: flex;
    font-size: 0.875rem;
    justify-content:space-between;
    p {
      margin: 0;
    }
    .brand {
      width:120px;
      height: 22px;
    }
    .letter--animated {
        -webkit-transform: translatey(0px);
        transform: translatey(0px)
    }

    .letter--animated.letter--o {
        -webkit-animation: float 10s ease-in-out infinite;
        animation: float 10s ease-in-out infinite
    }

    .letter--animated.letter--u {
        -webkit-animation: float 12s ease-in-out infinite;
        animation: float 12s ease-in-out infinite
    }

    .letter--animated.letter--r {
        -webkit-animation: float 11s ease-in-out infinite;
        animation: float 11s ease-in-out infinite
    }

    .letter--animated.letter--e {
        -webkit-animation: float 13s ease-in-out infinite;
        animation: float 13s ease-in-out infinite
    }

    svg:not(:root) {
        overflow: visible
    }

    @-webkit-keyframes float {
        0% {
            -webkit-transform: translatey(0px);
            transform: translatey(0px)
        }

        50% {
            -webkit-transform: translatey(10px);
            transform: translatey(10px)
        }

        100% {
            -webkit-transform: translatey(0px);
            transform: translatey(0px)
        }
    }

    @keyframes float {
        0% {
            -webkit-transform: translatey(0px);
            transform: translatey(0px)
        }

        50% {
            -webkit-transform: translatey(10px);
            transform: translatey(10px)
        }

        100% {
            -webkit-transform: translatey(0px);
            transform: translatey(0px)
        }
    }


  }
  header {
    position: fixed;
    width: 100%;
    display: flex;
    justify-content: space-between;
    .nav {
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      i {
      padding-top: 4px;
        transition: all 0.2s ease-in-out;
      }
      
      &:hover i {
        transform: scale(1.2)
      }
    }
    .header {
      padding: 20px;
      display: grid;
      grid-template-columns: 50px 1fr;
      h1 {
        margin: 0 0 10px;
        line-height: 1;
        font-size:2rem;
        font-family: 'NeuzeitGrotesk-Bold', sans-serif;
      }
    }
    .social {
      padding: 20px;
      h3 {
        margin: 0;
        font-size: 1rem;
        line-height: 0.5;
        display: inline-block;
      }
      a {
        font-size: 1.5rem;
        line-height: 1;
        display: inline-block;
        margin-left: 14px;
      }
    }
    .info dl {
      display: grid;
      grid-template-columns: 100px 1fr;
      margin: 0;
      dt, dd {
        margin: 0;
        padding: 0;
      }
    }
  }
  .face:hover {
    cursor: pointer;
  }
  text {
    color: white;
    fill: white;
    font-size: 0.8rem;
    cursor: default;
  }
  path.axis {
    fill: none;
    stroke: #666;
  }
  path.axis,
  text.axis {
    opacity: 0.3;
  }
</style>
